import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, IconButton, Tooltip } from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Repeat as RepeatIcon,
  Replay5 as Replay5Icon,
  Forward5 as Forward5Icon,
} from '@mui/icons-material';

/** Imperative handle exposed to the parent so it can move focus to the Play button (Task 4.3). */
export interface VideoPlayerControlsRef {
  focusPlay: () => void;
}

interface VideoPlayerControlsProps {
  /** Whether the player is currently playing (determines Play vs Pause icon). */
  isPlaying: boolean;
  /** Callback to start playback. */
  onPlay: () => void;
  /** Callback to pause playback. */
  onPause: () => void;
  /** Whether loop mode is currently active. */
  isLooping: boolean;
  /** Callback to toggle loop on/off. */
  onLoopToggle: () => void;
  /** Callback to seek back 5 seconds. */
  onSkipBack: () => void;
  /** Callback to seek forward 5 seconds. */
  onSkipForward: () => void;
  /** When true, no loopable segment covers the current playhead. */
  loopDisabled?: boolean;
}

/**
 * VideoPlayerControls — study-context controls surface for the YouTube player page.
 *
 * Story 2.3 (Task 4.1, 4.2, 4.3):
 * - Primary Play/Pause control near the study context (Task 4.1).
 * - Loop toggle with primary.main + primary.light active state; aria-pressed (AC 1, Task 4.2).
 * - Exposes focusPlay() so the page can move focus here after chip selection (Task 4.3).
 * - Touch targets >= 44×44px; no red/error color, no toast, no animation.
 */
export const VideoPlayerControls = forwardRef<VideoPlayerControlsRef, VideoPlayerControlsProps>(
  ({ isPlaying, onPlay, onPause, isLooping, onLoopToggle, onSkipBack, onSkipForward, loopDisabled }, ref) => {
    const { t } = useTranslation('player');
    const playButtonRef = useRef<HTMLButtonElement>(null);

    // Expose focusPlay() so VideoPlayer can move focus here on chip selection (Task 4.3).
    useImperativeHandle(ref, () => ({
      focusPlay: () => playButtonRef.current?.focus(),
    }));

    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default' }}
      >
        {/* Skip back 5 seconds */}
        <Tooltip title={t('skipBack5')}>
          <IconButton
            onClick={onSkipBack}
            aria-label={t('skipBack5')}
            size="medium"
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <Replay5Icon />
          </IconButton>
        </Tooltip>

        {/* Primary Play/Pause control (Task 4.1) */}
        <Tooltip title={isPlaying ? t('pause') : t('play')}>
          <IconButton
            ref={playButtonRef}
            onClick={isPlaying ? onPause : onPlay}
            aria-label={isPlaying ? t('pause') : t('play')}
            size="medium"
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Tooltip>

        {/* Skip forward 5 seconds */}
        <Tooltip title={t('skipForward5')}>
          <IconButton
            onClick={onSkipForward}
            aria-label={t('skipForward5')}
            size="medium"
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <Forward5Icon />
          </IconButton>
        </Tooltip>

        {/* Loop toggle (AC 1, Task 4.2) — primary.main + primary.light; no red, no animation */}
        <Tooltip title={t('loopSegment')}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton
              onClick={onLoopToggle}
              aria-pressed={isLooping}
              aria-label={t('loopSegment')}
              size="medium"
              disabled={loopDisabled}
              sx={{
                minWidth: 44,
                minHeight: 44,
                ...(isLooping && {
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'primary.light' },
                }),
              }}
            >
              <RepeatIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  },
);

VideoPlayerControls.displayName = 'VideoPlayerControls';
